/**
 * Generato automaticamente.
 * framework-cfg-utenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  DeleteUtentiIdParams,
  GetUtentiIdParams,
  GetUtentiParams,
  IntegraInformazioniUtenteRequest,
  PutUtentiDisabilitaParams,
  Utente,
  UtenteRequest,
  WsRispostaPaginataListUtente,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Cerca una serie di utenti. Ordinabili per id, codiceFiscale, nome, cognome, modificabile, email, pec, abilitato, idUtenteEsterno
 * @summary Cerca una serie di utenti. Ordinabili per id, codiceFiscale, nome, cognome, modificabile, email, pec, abilitato, idUtenteEsterno
 */
export const getUtenti = (
  params?: GetUtentiParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListUtente>(
    { url: `/framework-cfg-utenti/utenti`, method: 'get', params, signal },
    options,
  );
};

export const getGetUtentiQueryKey = (params?: GetUtentiParams) =>
  [`/framework-cfg-utenti/utenti`, ...(params ? [params] : [])] as const;

export const getGetUtentiQueryOptions = <
  TData = Awaited<ReturnType<typeof getUtenti>>,
  TError = ErrorType<unknown>,
>(
  params?: GetUtentiParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUtenti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getUtenti>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUtentiQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUtenti>>> = ({
    signal,
  }) => getUtenti(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetUtentiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUtenti>>
>;
export type GetUtentiQueryError = ErrorType<unknown>;

/**
 * @summary Cerca una serie di utenti. Ordinabili per id, codiceFiscale, nome, cognome, modificabile, email, pec, abilitato, idUtenteEsterno
 */
export const useGetUtenti = <
  TData = Awaited<ReturnType<typeof getUtenti>>,
  TError = ErrorType<unknown>,
>(
  params?: GetUtentiParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUtenti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetUtentiQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna un utente
 * @summary Aggiorna un utente
 */
export const putUtenti = (
  utenteRequest: UtenteRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-utenti/utenti`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: utenteRequest,
    },
    options,
  );
};

export const getPutUtentiMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putUtenti>>,
    TError,
    { data: UtenteRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putUtenti>>,
  TError,
  { data: UtenteRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putUtenti>>,
    { data: UtenteRequest }
  > = props => {
    const { data } = props ?? {};

    return putUtenti(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutUtentiMutationResult = NonNullable<
  Awaited<ReturnType<typeof putUtenti>>
>;
export type PutUtentiMutationBody = UtenteRequest;
export type PutUtentiMutationError = ErrorType<unknown>;

/**
 * @summary Aggiorna un utente
 */
export const usePutUtenti = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putUtenti>>,
    TError,
    { data: UtenteRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutUtentiMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Inserisce un utente
 * @summary Inserisce un utente
 */
export const postUtenti = (
  utenteRequest: UtenteRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cfg-utenti/utenti`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: utenteRequest,
    },
    options,
  );
};

export const getPostUtentiMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postUtenti>>,
    TError,
    { data: UtenteRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postUtenti>>,
  TError,
  { data: UtenteRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postUtenti>>,
    { data: UtenteRequest }
  > = props => {
    const { data } = props ?? {};

    return postUtenti(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostUtentiMutationResult = NonNullable<
  Awaited<ReturnType<typeof postUtenti>>
>;
export type PostUtentiMutationBody = UtenteRequest;
export type PostUtentiMutationError = ErrorType<unknown>;

/**
 * @summary Inserisce un utente
 */
export const usePostUtenti = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postUtenti>>,
    TError,
    { data: UtenteRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostUtentiMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Chiamata per aggiornare informazioni base utente
 * @summary Chiamata per aggiornare informazioni base utente
 */
export const putUtentiIntegraInfoBase = (
  integraInformazioniUtenteRequest: IntegraInformazioniUtenteRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-utenti/utenti/integra-info-base`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: integraInformazioniUtenteRequest,
    },
    options,
  );
};

export const getPutUtentiIntegraInfoBaseMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putUtentiIntegraInfoBase>>,
    TError,
    { data: IntegraInformazioniUtenteRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putUtentiIntegraInfoBase>>,
  TError,
  { data: IntegraInformazioniUtenteRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putUtentiIntegraInfoBase>>,
    { data: IntegraInformazioniUtenteRequest }
  > = props => {
    const { data } = props ?? {};

    return putUtentiIntegraInfoBase(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutUtentiIntegraInfoBaseMutationResult = NonNullable<
  Awaited<ReturnType<typeof putUtentiIntegraInfoBase>>
>;
export type PutUtentiIntegraInfoBaseMutationBody =
  IntegraInformazioniUtenteRequest;
export type PutUtentiIntegraInfoBaseMutationError = ErrorType<unknown>;

/**
 * @summary Chiamata per aggiornare informazioni base utente
 */
export const usePutUtentiIntegraInfoBase = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putUtentiIntegraInfoBase>>,
    TError,
    { data: IntegraInformazioniUtenteRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutUtentiIntegraInfoBaseMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Disabilita un utente attraverso uno dei seguenti parametri: id, codiceFiscale, utenteEsternoId
 * @summary Disabilita un utente
 */
export const putUtentiDisabilita = (
  params: PutUtentiDisabilitaParams,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    { url: `/framework-cfg-utenti/utenti/disabilita`, method: 'put', params },
    options,
  );
};

export const getPutUtentiDisabilitaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putUtentiDisabilita>>,
    TError,
    { params: PutUtentiDisabilitaParams },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putUtentiDisabilita>>,
  TError,
  { params: PutUtentiDisabilitaParams },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putUtentiDisabilita>>,
    { params: PutUtentiDisabilitaParams }
  > = props => {
    const { params } = props ?? {};

    return putUtentiDisabilita(params, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutUtentiDisabilitaMutationResult = NonNullable<
  Awaited<ReturnType<typeof putUtentiDisabilita>>
>;

export type PutUtentiDisabilitaMutationError = ErrorType<unknown>;

/**
 * @summary Disabilita un utente
 */
export const usePutUtentiDisabilita = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putUtentiDisabilita>>,
    TError,
    { params: PutUtentiDisabilitaParams },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutUtentiDisabilitaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * esegue l'update dei soli campi inseriti
 * @summary update puntuale
 */
export const postUtentiPuntuale = (
  utenteRequest: UtenteRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<Utente>(
    {
      url: `/framework-cfg-utenti/utenti/puntuale`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: utenteRequest,
    },
    options,
  );
};

export const getPostUtentiPuntualeMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postUtentiPuntuale>>,
    TError,
    { data: UtenteRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postUtentiPuntuale>>,
  TError,
  { data: UtenteRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postUtentiPuntuale>>,
    { data: UtenteRequest }
  > = props => {
    const { data } = props ?? {};

    return postUtentiPuntuale(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostUtentiPuntualeMutationResult = NonNullable<
  Awaited<ReturnType<typeof postUtentiPuntuale>>
>;
export type PostUtentiPuntualeMutationBody = UtenteRequest;
export type PostUtentiPuntualeMutationError = ErrorType<unknown>;

/**
 * @summary update puntuale
 */
export const usePostUtentiPuntuale = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postUtentiPuntuale>>,
    TError,
    { data: UtenteRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostUtentiPuntualeMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * verifica che la cambinazione email-password sia corretta
 * @summary Verifica password
 */
export const postUtentiConfermaEmail = (
  email: string,
  postUtentiConfermaEmailBody: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<Utente>(
    {
      url: `/framework-cfg-utenti/utenti/conferma/${email}`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: postUtentiConfermaEmailBody,
    },
    options,
  );
};

export const getPostUtentiConfermaEmailMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postUtentiConfermaEmail>>,
    TError,
    { email: string; data: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postUtentiConfermaEmail>>,
  TError,
  { email: string; data: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postUtentiConfermaEmail>>,
    { email: string; data: string }
  > = props => {
    const { email, data } = props ?? {};

    return postUtentiConfermaEmail(email, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostUtentiConfermaEmailMutationResult = NonNullable<
  Awaited<ReturnType<typeof postUtentiConfermaEmail>>
>;
export type PostUtentiConfermaEmailMutationBody = string;
export type PostUtentiConfermaEmailMutationError = ErrorType<unknown>;

/**
 * @summary Verifica password
 */
export const usePostUtentiConfermaEmail = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postUtentiConfermaEmail>>,
    TError,
    { email: string; data: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostUtentiConfermaEmailMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Restituisce utente a partire da id
 * @summary Restituisce utente a partire da id
 */
export const getUtentiId = (
  id: string,
  params?: GetUtentiIdParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<Utente>(
    {
      url: `/framework-cfg-utenti/utenti/${id}`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetUtentiIdQueryKey = (
  id: string,
  params?: GetUtentiIdParams,
) =>
  [`/framework-cfg-utenti/utenti/${id}`, ...(params ? [params] : [])] as const;

export const getGetUtentiIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getUtentiId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  params?: GetUtentiIdParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUtentiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getUtentiId>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUtentiIdQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUtentiId>>> = ({
    signal,
  }) => getUtentiId(id, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetUtentiIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUtentiId>>
>;
export type GetUtentiIdQueryError = ErrorType<unknown>;

/**
 * @summary Restituisce utente a partire da id
 */
export const useGetUtentiId = <
  TData = Awaited<ReturnType<typeof getUtentiId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  params?: GetUtentiIdParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUtentiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetUtentiIdQueryOptions(id, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Cancella un utente attraverso il suo id
 * @summary Cancella un utente
 */
export const deleteUtentiId = (
  id: string,
  params?: DeleteUtentiIdParams,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    { url: `/framework-cfg-utenti/utenti/${id}`, method: 'delete', params },
    options,
  );
};

export const getDeleteUtentiIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteUtentiId>>,
    TError,
    { id: string; params?: DeleteUtentiIdParams },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteUtentiId>>,
  TError,
  { id: string; params?: DeleteUtentiIdParams },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteUtentiId>>,
    { id: string; params?: DeleteUtentiIdParams }
  > = props => {
    const { id, params } = props ?? {};

    return deleteUtentiId(id, params, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteUtentiIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteUtentiId>>
>;

export type DeleteUtentiIdMutationError = ErrorType<unknown>;

/**
 * @summary Cancella un utente
 */
export const useDeleteUtentiId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteUtentiId>>,
    TError,
    { id: string; params?: DeleteUtentiIdParams },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteUtentiIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Restituisce utente a partire dalla email
 * @summary Restituisce utente a partire dalla email
 */
export const getUtentiEmailEmail = (
  email: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<Utente>(
    {
      url: `/framework-cfg-utenti/utenti/email/${email}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetUtentiEmailEmailQueryKey = (email: string) =>
  [`/framework-cfg-utenti/utenti/email/${email}`] as const;

export const getGetUtentiEmailEmailQueryOptions = <
  TData = Awaited<ReturnType<typeof getUtentiEmailEmail>>,
  TError = ErrorType<unknown>,
>(
  email: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUtentiEmailEmail>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getUtentiEmailEmail>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetUtentiEmailEmailQueryKey(email);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getUtentiEmailEmail>>
  > = ({ signal }) => getUtentiEmailEmail(email, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!email, ...queryOptions };
};

export type GetUtentiEmailEmailQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUtentiEmailEmail>>
>;
export type GetUtentiEmailEmailQueryError = ErrorType<unknown>;

/**
 * @summary Restituisce utente a partire dalla email
 */
export const useGetUtentiEmailEmail = <
  TData = Awaited<ReturnType<typeof getUtentiEmailEmail>>,
  TError = ErrorType<unknown>,
>(
  email: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUtentiEmailEmail>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetUtentiEmailEmailQueryOptions(email, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce utente a partire da username (id utente esterno)
 * @summary Restituisce utente a partire da username (id utente esterno)
 */
export const getUtentiByIdUtenteEsternoFromUsernameUsername = (
  username: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<Utente>(
    {
      url: `/framework-cfg-utenti/utenti/byIdUtenteEsternoFromUsername/${username}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetUtentiByIdUtenteEsternoFromUsernameUsernameQueryKey = (
  username: string,
) =>
  [
    `/framework-cfg-utenti/utenti/byIdUtenteEsternoFromUsername/${username}`,
  ] as const;

export const getGetUtentiByIdUtenteEsternoFromUsernameUsernameQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getUtentiByIdUtenteEsternoFromUsernameUsername>
  >,
  TError = ErrorType<unknown>,
>(
  username: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<typeof getUtentiByIdUtenteEsternoFromUsernameUsername>
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getUtentiByIdUtenteEsternoFromUsernameUsername>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetUtentiByIdUtenteEsternoFromUsernameUsernameQueryKey(username);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getUtentiByIdUtenteEsternoFromUsernameUsername>>
  > = ({ signal }) =>
    getUtentiByIdUtenteEsternoFromUsernameUsername(
      username,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!username, ...queryOptions };
};

export type GetUtentiByIdUtenteEsternoFromUsernameUsernameQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getUtentiByIdUtenteEsternoFromUsernameUsername>>
  >;
export type GetUtentiByIdUtenteEsternoFromUsernameUsernameQueryError =
  ErrorType<unknown>;

/**
 * @summary Restituisce utente a partire da username (id utente esterno)
 */
export const useGetUtentiByIdUtenteEsternoFromUsernameUsername = <
  TData = Awaited<
    ReturnType<typeof getUtentiByIdUtenteEsternoFromUsernameUsername>
  >,
  TError = ErrorType<unknown>,
>(
  username: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<typeof getUtentiByIdUtenteEsternoFromUsernameUsername>
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetUtentiByIdUtenteEsternoFromUsernameUsernameQueryOptions(
      username,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce utente a partire da CF
 * @summary Restituisce utente a partire da CF
 */
export const getUtentiByCodiceFiscaleCodiceFiscale = (
  codiceFiscale: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<Utente>(
    {
      url: `/framework-cfg-utenti/utenti/byCodiceFiscale/${codiceFiscale}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetUtentiByCodiceFiscaleCodiceFiscaleQueryKey = (
  codiceFiscale: string,
) => [`/framework-cfg-utenti/utenti/byCodiceFiscale/${codiceFiscale}`] as const;

export const getGetUtentiByCodiceFiscaleCodiceFiscaleQueryOptions = <
  TData = Awaited<ReturnType<typeof getUtentiByCodiceFiscaleCodiceFiscale>>,
  TError = ErrorType<unknown>,
>(
  codiceFiscale: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUtentiByCodiceFiscaleCodiceFiscale>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getUtentiByCodiceFiscaleCodiceFiscale>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetUtentiByCodiceFiscaleCodiceFiscaleQueryKey(codiceFiscale);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getUtentiByCodiceFiscaleCodiceFiscale>>
  > = ({ signal }) =>
    getUtentiByCodiceFiscaleCodiceFiscale(
      codiceFiscale,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!codiceFiscale, ...queryOptions };
};

export type GetUtentiByCodiceFiscaleCodiceFiscaleQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUtentiByCodiceFiscaleCodiceFiscale>>
>;
export type GetUtentiByCodiceFiscaleCodiceFiscaleQueryError =
  ErrorType<unknown>;

/**
 * @summary Restituisce utente a partire da CF
 */
export const useGetUtentiByCodiceFiscaleCodiceFiscale = <
  TData = Awaited<ReturnType<typeof getUtentiByCodiceFiscaleCodiceFiscale>>,
  TError = ErrorType<unknown>,
>(
  codiceFiscale: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUtentiByCodiceFiscaleCodiceFiscale>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetUtentiByCodiceFiscaleCodiceFiscaleQueryOptions(
    codiceFiscale,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Cancella un utente fisicamente attraverso il suo id
 * @summary Cancella un utente fisicamente
 */
export const deleteUtentiIdForce = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    { url: `/framework-cfg-utenti/utenti/${id}/force`, method: 'delete' },
    options,
  );
};

export const getDeleteUtentiIdForceMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteUtentiIdForce>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteUtentiIdForce>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteUtentiIdForce>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteUtentiIdForce(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteUtentiIdForceMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteUtentiIdForce>>
>;

export type DeleteUtentiIdForceMutationError = ErrorType<unknown>;

/**
 * @summary Cancella un utente fisicamente
 */
export const useDeleteUtentiIdForce = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteUtentiIdForce>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteUtentiIdForceMutationOptions(options);

  return useMutation(mutationOptions);
};
